import { put, takeLatest } from "redux-saga/effects";
import {
    getShowsByDateRangeSuccess,
    createShowSuccess,
    updateShowSuccess,
    getShowByIdSuccess,
    deleteShowSuccess,
    createMonthlyDefaultShowsSuccess,
    updateGlobalMaxCapacitySuccess,
    getShowsByDateRangeFailed,
    getShowByIdFailed,
    createShowFailed,
    updateShowFailed,
    deleteShowFailed,
    createMonthlyDefaultShowsFailed,
    updateGlobalMaxCapacityFailed,
    initializeHistoricalShowsSuccess,
    initializeHistoricalShowsFailed,
    resetAvailabilitySuccess,
    resetAvailabilityFailed,
    updateJobStatus,
    recalibrateTimesFailed,
    recalibrateTimesSuccess,
    syncShowCapacitySuccess,
    syncShowCapacityFailed,
    syncShowCapacityByIdSuccess,
    syncShowCapacityByIdFailed,
} from "../reducers/showSlice";
import { axiosInstance } from "../../config/axios";
import { apiEndpoints } from "../../utils/api-endpoints";
import { createErrorAction } from '../reducers/errorSlice';
import { setSuccess } from '../reducers/successSlice';
import { updateSettingSuccess } from "../reducers/settingSlice";
import { SettingKey } from "../../models/setting.model";
import { getErrorMessage } from "../../utils/helper.utils";
import { delay } from "redux-saga/effects";

function* getShowsByDateRange(action: any): any {
    try {
        const params = action.payload || {};
        const response = yield axiosInstance.get(apiEndpoints.show.dateRange, { params });
        yield put(getShowsByDateRangeSuccess(response.data));
    } catch (error) {
        yield put(getShowsByDateRangeFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/getShowsByDateRange'));
    }
}

function* getShowById(action: any): any {
    try {
        const response = yield axiosInstance.get(apiEndpoints.show.byId(action.payload));
        yield put(getShowByIdSuccess(response.data));
    } catch (error) {
        yield put(getShowByIdFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/getShowById'));
    }
}

function* createShow(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.show.create, action.payload);
        yield put(createShowSuccess(response.data));
    } catch (error) {
        yield put(createShowFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/createShow'));
    }
}

function* updateShow(action: any): any {
    try {
        const { id, data } = action.payload;
        const response = yield axiosInstance.put(apiEndpoints.show.update(id), data);
        yield put(updateShowSuccess(response.data));
    } catch (error) {
        yield put(updateShowFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/updateShow'));
    }
}

function* deleteShow(action: any): any {
    try {
        const response = yield axiosInstance.delete(apiEndpoints.show.delete(action.payload));
        yield put(deleteShowSuccess(response.data));
    } catch (error) {
        yield put(deleteShowFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/deleteShow'));
    }
}

function* createMonthlyDefaultShows(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.show.createMonthlyDefaultShows, action.payload);
        yield put(createMonthlyDefaultShowsSuccess(response.data));
    } catch (error) {
        yield put(createMonthlyDefaultShowsFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/createMonthlyDefaultShows'));
    }
}

function* updateGlobalMaxCapacity(action: any): any {
    try {
        const { maxCapacity } = action.payload;
        const response = yield axiosInstance.put(apiEndpoints.show.updateGlobalCapacity, { maxCapacity });
        yield put(updateGlobalMaxCapacitySuccess(response.data));
        yield put(updateSettingSuccess({
            setting_key: SettingKey.DEFAULT_MAX_CAPACITY,
            setting_value: maxCapacity,
        } as any));
    } catch (error) {
        yield put(updateGlobalMaxCapacityFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/updateMaxCapacity'));
    }
}

function* initializeHistoricalShows(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.show.initializeHistorical, action.payload);
        yield put(initializeHistoricalShowsSuccess(response.data));

        const jobId = response.data.jobId;
        while (true) {
            const jobResponse = yield axiosInstance.get(apiEndpoints.show.jobStatus(jobId));
            yield put(updateJobStatus(jobResponse.data));

            if (['completed', 'failed'].includes(jobResponse.data.status)) {
                break;
            }
            yield delay(2000);
        }
    } catch (error) {
        yield put(initializeHistoricalShowsFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/initializeHistoricalShows'));
    }
}

function* resetAvailability(): any {
    try {
        yield axiosInstance.post(apiEndpoints.show.resetAvailabilities);
        yield put(resetAvailabilitySuccess());
        yield put(getShowsByDateRange({}));
    } catch (error) {
        yield put(resetAvailabilityFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/resetShowAvailabilities'));
    }
}

function* recalibrateTimes(): any {
    try {
        yield axiosInstance.post(apiEndpoints.show.recalibrateTimes);
        yield put(recalibrateTimesSuccess());
        yield put(getShowsByDateRange({}));
    } catch (error) {
        yield put(recalibrateTimesFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/recalibrateTimes'));
    }
}

function* syncShowCapacitySaga(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.show.syncCapacityManual);
        yield put(syncShowCapacitySuccess());
        yield put(setSuccess(response.data.message || 'Show capacity synchronized successfully!'));
        const refetchParams = action.payload ? { ...action.payload } : {};
        yield put({ type: 'show/getShowsByDateRange', payload: refetchParams });
    } catch (error) {
        yield put(syncShowCapacityFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'show/syncShowCapacity'));
    }
}

function* syncShowCapacityByIdSaga(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.show.syncCapacityById(action.payload));
        yield put(syncShowCapacityByIdSuccess(response.data));
        yield put(setSuccess('Show capacity synced successfully!'));
    } catch (error) {
        yield put(syncShowCapacityByIdFailed());
        yield put(createErrorAction(error, 'show/syncShowCapacityById'));
    }
}

export function* watchShowSaga() {
    yield takeLatest('show/getShowsByDateRange', getShowsByDateRange);
    yield takeLatest('show/getShowById', getShowById);
    yield takeLatest('show/createShow', createShow);
    yield takeLatest('show/updateShow', updateShow);
    yield takeLatest('show/deleteShow', deleteShow);
    yield takeLatest('show/createMonthlyDefaultShows', createMonthlyDefaultShows);
    yield takeLatest('show/updateGlobalMaxCapacity', updateGlobalMaxCapacity);
    yield takeLatest('show/initializeHistoricalShows', initializeHistoricalShows);
    yield takeLatest('show/resetAvailability', resetAvailability);
    yield takeLatest('show/recalibrateTimes', recalibrateTimes);
    yield takeLatest('show/syncShowCapacity', syncShowCapacitySaga);
    yield takeLatest('show/syncShowCapacityById', syncShowCapacityByIdSaga);
} 